import type { AnnualDetail, AnnualInstallment, AnnualState, AnnualSummary, SatDocumentKind } from '../model/period';
import type { MonthMark, Standing } from './yearStanding';
import { formatDateFull, formatDayLong, formatDayShort, formatHour, formatPesos } from './formatters';
import type { AmountFact, AmountMood, CycleStage, SatDocument } from './periodPresentation';
import { refundStatusLabel } from './refundPresentation';

const REASON_COPY: Record<string, string> = {
	opinion: 'El SAT la tiene marcada como pendiente',
	'612': 'Por tu actividad empresarial',
	'606': 'Por tu arrendamiento',
	'626': 'Por RESICO',
	'625_provisional': 'Por tus ingresos en plataformas',
	'625_ingreso_anterior_300k': 'El año pasado pasaste de 300 mil en plataformas',
	'625_otro_ingreso_anterior': 'Por tus ingresos en plataformas',
	dividendos: 'Recibiste dividendos',
	sueldos_dos_patrones: 'Tuviste dos patrones al mismo tiempo',
	sueldos_400k: 'Tus sueldos pasaron de 400 mil',
	sueldos_400k_isr_a_cargo: 'Tus sueldos pasaron de 400 mil y hay impuesto por pagar',
	sueldos_400k_sin_tarifa: 'Tus sueldos pasaron de 400 mil',
	sueldos_indemnizacion_o_jubilacion: 'Recibiste una indemnización o jubilación',
	sueldos_baja_antes_de_diciembre: 'Cambiaste de trabajo o dejaste de trabajar antes de diciembre',
	sueldos_cambio_de_patron: 'Cambiaste de trabajo este año',
	intereses_100k: 'Por tus intereses',
	sueldos_e_intereses: 'Por tus sueldos e intereses',
	resico_3_omisiones: 'Dejaste de declarar tres meses o más',
	resico_3_5m: 'Pasaste el tope de RESICO',
	resico_liberado_3_13_7: 'Con RESICO al día no presentas anual',
	'625_definitivo': 'Tu retención en plataformas ya fue el pago definitivo',
	sueldos_un_patron_3_17_11: 'Con un solo patrón y sin impuesto a cargo no estás obligado',
	sueldos_un_patron_bajo_400k: 'Con un solo patrón y menos de 400 mil no estás obligado',
};

const VOLUNTARY_PITCH: Record<string, string> = {
	voluntaria_deducciones_personales:
		'Tienes facturas de deducciones personales y con ellas podrías tener saldo a favor: el SAT te regresaría parte de lo que te retuvieron.',
	voluntaria_sueldos_saldo_a_favor: 'Con lo que te retuvo tu patrón podrías tener saldo a favor, y el SAT te regresaría la diferencia.',
	voluntaria_intereses_retenidos: 'El banco te retuvo impuesto por tus intereses y podrías tener saldo a favor.',
};

const NOT_REQUIRED_REASONS = new Set([
	...Object.keys(VOLUNTARY_PITCH),
	'resico_liberado_3_13_7',
	'625_definitivo',
	'sueldos_un_patron_3_17_11',
	'sueldos_un_patron_bajo_400k',
]);

export const isVoluntaryAnnual = (annual: Pick<AnnualSummary, 'reasons'>) => {
	const reasons = annual.reasons ?? [];
	return reasons.length > 0 && reasons.every((reason) => NOT_REQUIRED_REASONS.has(reason));
};

export const voluntaryPitchOf = (annual: Pick<AnnualSummary, 'reasons'>) =>
	Object.entries(VOLUNTARY_PITCH).find(([reason]) => annual.reasons?.includes(reason))?.[1] ??
	'Si te retuvieron de más, podrías tener saldo a favor y el SAT te regresaría la diferencia.';

export const annualReasonText = (annual: Pick<AnnualSummary, 'reasons'>) => {
	const texts = (annual.reasons ?? []).map((reason) => REASON_COPY[reason]).filter(Boolean);
	return texts.length > 0
		? texts
				.slice(0, 2)
				.map((text, index) => (index === 0 ? text : text.charAt(0).toLowerCase() + text.slice(1)))
				.join(' y ')
		: undefined;
};

export const ANNUAL_PROMISES = [
	'Juntamos tus ingresos y retenciones de los 12 meses.',
	'Sumamos tus deducciones personales para que pagues menos o el SAT te devuelva.',
	'La presentamos ante el SAT y te avisamos el resultado.',
];

export const DEDUCTIONS_HINT =
	'Recuerda pedir factura de médicos, dentistas, lentes, hospitales, colegiaturas e intereses de tu hipoteca: todo suma para tu anual.';

export const DEDUCTIONS_TIP =
	'Guarda las facturas de médicos, dentistas, lentes, hospitales, colegiaturas e intereses de tu crédito hipotecario. Pueden hacer que el SAT te devuelva dinero.';

export const daysUntil = (date: string, today = new Date()) => {
	const target = new Date(`${date}T23:59:59`);
	return Math.max(0, Math.ceil((target.getTime() - today.getTime()) / 86_400_000));
};

const sumOf = (installments: AnnualInstallment[]) => installments.reduce((total, installment) => total + installment.amount, 0);

export const installmentPlanOf = (installments: AnnualInstallment[] = []) => {
	const paid = installments.filter((installment) => installment.status === 'pagada');
	const unpaid = installments.filter((installment) => installment.status !== 'pagada');
	const next =
		unpaid.find((installment) => installment.status === 'vencida') ?? unpaid.find((installment) => installment.status === 'siguiente');
	return {
		count: installments.length,
		paidCount: paid.length,
		paidAmount: sumOf(paid),
		remaining: sumOf(unpaid),
		total: sumOf(installments),
		next,
		overdue: next?.status === 'vencida',
		active: unpaid.length > 0,
	};
};

export type InstallmentPlan = ReturnType<typeof installmentPlanOf>;

export type AnnualCta = 'comprar' | 'ponerme_al_corriente' | 'copiar_linea';

export type AnnualPresentation = {
	subtitle?: string;
	statusTitle: string;
	statusTone: 'success' | 'error' | 'information' | 'neutral';
	message?: string;
	amount?: { label: string; value: number | null; mood: AmountMood; note?: string; facts?: AmountFact[] };
	stages?: { stages: CycleStage[]; current: number; done: boolean };
	cta?: AnnualCta;
	showPromises: boolean;
};

const balanceNote = (detail: AnnualDetail) => {
	const balance = detail.balance;
	if (!balance || detail.refund) return undefined;
	if (balance.option === 'devolucion') {
		const account = [balance.bank, balance.clabeLast4 ? `terminación ${balance.clabeLast4}` : undefined].filter(Boolean).join(' ');
		return account ? `Lo pediste en devolución a tu cuenta ${account}.` : 'Lo pediste en devolución.';
	}
	if (balance.option === 'compensacion') return 'Lo elegiste para compensarlo contra tus próximos pagos de ISR.';
	return 'No elegiste qué hacer con tu saldo a favor. Puedes pedirlo en devolución con una complementaria.';
};

const presentedSubtitle = (detail: AnnualDetail) => {
	if (!detail.presentedOn) return undefined;
	return `Presentada el ${formatDateFull(detail.presentedOn)}${detail.filing?.byHeru ? ', la presentamos por ti' : ''}`;
};

const incomeNoteOf = (annual: AnnualSummary) => {
	const salary = annual.salaryIncome ?? 0;
	if (salary > 0 && salary >= annual.income) return 'Es lo que te han pagado tus patrones este año.';
	if (salary > 0) return 'Es lo que llevas facturado, lo que te reportaron las plataformas y tu nómina.';
	return 'Es lo que llevas facturado y lo que te reportaron las plataformas.';
};

const PAYING_STAGES: CycleStage[] = ['calculamos', 'revisas', 'presentamos', 'pagas'];

const presentInstallments = (detail: AnnualDetail, plan: InstallmentPlan): AnnualPresentation => {
	const next = plan.next;
	return {
		subtitle: presentedSubtitle(detail),
		statusTitle: plan.overdue ? 'Se te pasó un pago' : 'Vas pagando en parcialidades',
		statusTone: plan.overdue ? 'error' : 'neutral',
		message: !next
			? undefined
			: plan.overdue
				? `El pago ${next.number} venció el ${formatDayShort(next.dueOn)} y no vemos que lo hayas pagado. Si ya lo pagaste, no tienes que hacer nada. Si no, conviene pagarlo pronto: los atrasos suman recargos.`
				: `Elegiste pagarla en ${plan.count} pagos. Llevas ${plan.paidCount} y el siguiente vence el ${formatDayShort(next.dueOn)}.`,
		amount: {
			label: 'Te falta pagar',
			value: plan.remaining,
			mood: 'a_pagar',
			facts: [
				{ label: 'Ya pagaste', value: plan.paidAmount },
				{ label: `Total en ${plan.count} pagos`, value: plan.total },
			],
		},
		stages: { stages: PAYING_STAGES, current: 3, done: false },
		cta: next?.captureLine && !plan.overdue ? 'copiar_linea' : undefined,
		showPromises: false,
	};
};

export const presentAnnual = (detail: AnnualDetail): AnnualPresentation => {
	const reason = annualReasonText(detail);
	const plan = installmentPlanOf(detail.installments);
	if (plan.active) return presentInstallments(detail, plan);
	switch (detail.state) {
		case 'en_curso':
			return {
				subtitle: isVoluntaryAnnual(detail)
					? `Sugerida · puedes presentarla en abril de ${detail.year + 1}`
					: `Se presenta en abril de ${detail.year + 1}`,
				statusTitle: 'Tu año va en curso',
				statusTone: 'information',
				message: 'La anual junta tus 12 meses. En cuanto cierre el año la armamos con tus datos del SAT.',
				amount: {
					label: 'Llevas de ingresos este año',
					value: detail.income,
					mood: 'en_proceso',
					note: incomeNoteOf(detail),
				},
				cta: 'comprar',
				showPromises: true,
			};
		case 'pendiente': {
			if (detail.purchased) {
				return {
					subtitle: reason,
					statusTitle: 'Ya la contrataste',
					statusTone: 'information',
					message: 'La armamos con tus datos del SAT y te avisamos en cuanto esté lista para que la revises.',
					stages: { stages: ['calculamos', 'revisas', 'presentamos'], current: 0, done: false },
					showPromises: true,
				};
			}
			const days = daysUntil(detail.deadline);
			return {
				subtitle: reason,
				statusTitle: 'Te falta presentarla',
				statusTone: 'error',
				message:
					days > 0
						? `Tienes hasta el ${formatDayLong(detail.deadline)}, faltan ${days} días. Presentarla a tiempo te evita multas y recargos.`
						: 'Ya pasó la fecha límite. Mientras más pronto la presentemos, menos multas y recargos.',
				stages: { stages: ['calculamos', 'revisas', 'presentamos'], current: 0, done: false },
				cta: 'ponerme_al_corriente',
				showPromises: true,
			};
		}
		case 'sin_obligacion':
			if (detail.year >= new Date().getFullYear()) {
				return {
					statusTitle: 'Hasta hoy no tienes que presentar tu anual',
					statusTone: 'success',
					message: reason
						? `${reason}. Si algo cambia en el año, te avisamos.`
						: 'Con lo que llevas este año no estás obligado. Si algo cambia, te avisamos.',
					showPromises: false,
				};
			}
			return {
				statusTitle: 'Este año no te tocaba presentarla',
				statusTone: 'success',
				message: reason ? `${reason}.` : `Con tus ingresos de ${detail.year} no estabas obligado a presentar la declaración anual.`,
				showPromises: false,
			};
		case 'sin_datos':
			return {
				statusTitle: `Todavía no tenemos tu anual de ${detail.year}`,
				statusTone: 'neutral',
				message: 'Si la presentaste, la verás aquí en cuanto el SAT nos comparta el acuse.',
				showPromises: false,
			};
		case 'por_pagar':
			return {
				subtitle: presentedSubtitle(detail),
				statusTitle: 'Solo falta pagarla',
				statusTone: 'neutral',
				amount: {
					label: 'A pagar',
					value: detail.total ?? null,
					mood: 'a_pagar',
					note: detail.captureLine ? `Tienes hasta el ${formatDayLong(detail.captureLine.validUntil)} para pagarla.` : undefined,
				},
				stages: { stages: PAYING_STAGES, current: 3, done: false },
				cta: detail.captureLine && !detail.captureLine.expired ? 'copiar_linea' : undefined,
				showPromises: false,
			};
		default:
			break;
	}
	const subtitle = presentedSubtitle(detail);
	if (detail.result === 'saldo_a_favor') {
		return {
			subtitle,
			statusTitle: 'Te quedó saldo a favor',
			statusTone: 'success',
			amount: { label: 'Saldo a favor', value: detail.total ?? 0, mood: 'a_favor', note: balanceNote(detail) },
			showPromises: false,
		};
	}
	if (detail.result === 'a_pagar') {
		return {
			subtitle,
			statusTitle: detail.paid ? 'Presentada y pagada' : 'Presentada',
			statusTone: detail.paid ? 'success' : 'neutral',
			amount: {
				label: detail.paid ? 'Pagaste' : 'A cargo',
				value: detail.total ?? 0,
				mood: detail.paid ? 'pagada' : 'a_pagar',
				note: detail.paymentUnconfirmed ? 'No tenemos el comprobante de pago. Si ya la pagaste, no tienes que hacer nada.' : undefined,
			},
			showPromises: false,
		};
	}
	return {
		subtitle,
		statusTitle: 'Presentada sin pagar nada',
		statusTone: 'success',
		amount: { label: 'Sin pagar nada', value: 0, mood: 'en_ceros' },
		showPromises: false,
	};
};

const DOCUMENT_TITLES: Record<SatDocumentKind, string> = {
	acuse: 'Acuse de la anual',
	detalle: 'Detalle de la anual',
	pago: 'Comprobante de pago',
	fed: 'Acuse de tu solicitud de devolución',
};

const documentDetailOf = (detail: AnnualDetail, kind: SatDocumentKind) => {
	if (kind === 'pago' && detail.payments[0]) return `Pagada el ${formatDayShort(detail.payments[0].paidOn)}`;
	const fed = detail.refund?.fed;
	if (kind === 'fed' && fed)
		return [fed.folio && `Folio ${fed.folio}`, `presentada el ${formatDayShort(fed.presentedOn)}`].filter(Boolean).join(', ');
	return `Ejercicio ${detail.year}`;
};

export const annualDocumentsOf = (detail: AnnualDetail): SatDocument[] =>
	(['acuse', 'detalle', 'pago', 'fed'] as SatDocumentKind[]).flatMap((kind) => {
		const url = detail.documents[kind];
		if (!url) return [];
		return [{ kind, title: DOCUMENT_TITLES[kind], detail: documentDetailOf(detail, kind), url }];
	});

const bankName = (raw: string) =>
	raw
		.replace(/^Banco\s+/i, '')
		.replace(/\s*\(México\)/i, '')
		.replace(/,?\s*S\.\s?A\..*$/i, '')
		.trim();

const BALANCE_OPTIONS: Record<NonNullable<AnnualDetail['balance']>['option'], string> = {
	devolucion: 'Lo pediste en devolución',
	compensacion: 'Lo compensas en tus pagos',
	sin_seleccion: 'No elegiste qué hacer',
};

export type AnnualFact = { label: string; value: string };

const paymentFactsOf = (detail: AnnualDetail): (AnnualFact | undefined)[] => {
	const plan = installmentPlanOf(detail.installments);
	if (plan.count > 0) return [{ label: 'Forma de pago', value: `En ${plan.count} parcialidades` }];
	if (detail.balance) {
		const account = [detail.balance.bank && bankName(detail.balance.bank), detail.balance.clabeLast4 && `···${detail.balance.clabeLast4}`]
			.filter(Boolean)
			.join(' ');
		return [
			{ label: 'Saldo a favor', value: BALANCE_OPTIONS[detail.balance.option] },
			account ? { label: 'Cuenta', value: account } : undefined,
		];
	}
	if (detail.result !== 'a_pagar') return [];
	const payment = detail.payments.at(-1);
	if (!payment) return [{ label: 'Pago', value: detail.state === 'por_pagar' ? 'Pendiente' : 'Sin comprobante' }];
	return [
		{ label: 'Pagada', value: formatDateFull(payment.paidOn) },
		payment.bank
			? {
					label: 'Banco',
					value: [bankName(payment.bank), payment.method && `por ${payment.method.toLowerCase()}`].filter(Boolean).join(', '),
				}
			: undefined,
		{ label: 'Monto pagado', value: formatPesos(detail.payments.reduce((total, current) => total + current.amount, 0)) },
	];
};

export const annualFactsOf = (detail: AnnualDetail): AnnualFact[] => {
	const filing = detail.filing;
	if (!filing) return [];
	const complementaries = filing.filings - 1;
	const facts: (AnnualFact | undefined)[] = [
		{ label: 'Presentada', value: `${formatDateFull(filing.presentedOn)}, ${formatHour(filing.presentedOn)}` },
		filing.byHeru ? { label: 'La presentó', value: 'heru, por ti' } : undefined,
		{ label: 'Tipo', value: filing.type === 'NORMAL' ? 'Normal' : 'Complementaria' },
		complementaries > 0
			? {
					label: 'Veces presentada',
					value: `${filing.filings}: la normal y ${complementaries} ${complementaries === 1 ? 'complementaria' : 'complementarias'}`,
				}
			: undefined,
		{ label: 'Número de operación', value: filing.operationNumber },
		...paymentFactsOf(detail),
	];
	return facts.filter((fact): fact is AnnualFact => fact !== undefined);
};

const ANNUAL_STANDING: Record<AnnualState, Standing> = {
	en_curso: 'en_curso',
	pendiente: 'sin_presentar',
	por_pagar: 'por_pagar',
	presentada: 'al_dia',
	sin_obligacion: 'al_dia',
	sin_datos: 'en_curso',
};

export const annualStandingOf = (annual: AnnualSummary): Standing =>
	annual.state === 'pendiente' && annual.purchased ? 'en_curso' : ANNUAL_STANDING[annual.state];

export const annualIsActionable = (annual: AnnualSummary) =>
	(annual.state === 'pendiente' && !annual.purchased) || annual.state === 'por_pagar';

const SEASON_LAST_MONTH = 3;

export const featuredAnnualOf = (
	last: AnnualSummary | undefined,
	current: AnnualSummary | undefined,
	today = new Date(),
	preferCurrent = false,
) => {
	if (preferCurrent && current) return current;
	if (last && (annualIsActionable(last) || today.getMonth() <= SEASON_LAST_MONTH)) return last;
	return current;
};

export const annualMarkOf = (annual: AnnualSummary): MonthMark => {
	const standing = annualStandingOf(annual);
	if (standing !== 'al_dia') return standing;
	if (annual.result === 'saldo_a_favor') return 'a_favor';
	return annual.result === 'a_pagar' ? 'pagada' : 'en_ceros';
};

export const annualStatusLabel = (annual: AnnualSummary) => {
	switch (annual.state) {
		case 'en_curso':
			return `Se presenta en abril de ${annual.year + 1}`;
		case 'pendiente':
			return annual.purchased ? 'Ya la contrataste · te avisamos cuando esté lista' : `Falta presentarla · vence el ${formatDayShort(annual.deadline)}`;
		case 'por_pagar': {
			const plan = installmentPlanOf(annual.installments);
			if (!plan.active || !plan.next) return 'Solo falta pagarla';
			return plan.overdue
				? `Se venció el pago ${plan.next.number} de ${plan.count}`
				: `Pago ${plan.next.number} de ${plan.count} · vence el ${formatDayShort(plan.next.dueOn)}`;
		}
		default:
			break;
	}
	if (annual.refundStatus) return refundStatusLabel(annual.refundStatus);
	if (annual.result === 'saldo_a_favor') return 'Te quedó saldo a favor';
	if (annual.result === 'a_pagar') return annual.paid ? 'Presentada y pagada' : 'Presentada';
	return 'Presentada, sin pagar nada';
};

export const annualAmountText = (annual: AnnualSummary) => {
	const plan = installmentPlanOf(annual.installments);
	if (plan.active && plan.next) return formatPesos(plan.next.amount);
	return annual.total !== undefined && annual.total > 0 ? formatPesos(annual.total) : undefined;
};
